/**
 * Top inset when aligning an EditableField to the scrollport
 * (matches {@link EDITABLE_FIELD_SCROLL_MARGIN_CLASS} ≈ 1–1.5rem).
 */
export const EDITABLE_FIELD_SCROLL_TOP_PADDING_PX = 24;

function isScrollableOverflow(value: string): boolean {
	return /(auto|scroll|overlay)/.test(value);
}

/**
 * Nearest ancestor that actually scrolls (overflow + taller content).
 * Falls back to `document.scrollingElement` when none is found.
 */
export function getEditableFieldScrollParent(
	el: HTMLElement,
): HTMLElement | null {
	let parent = el.parentElement;
	while (parent) {
		const style = getComputedStyle(parent);
		if (
			(isScrollableOverflow(style.overflowY) ||
				isScrollableOverflow(style.overflow)) &&
			parent.scrollHeight > parent.clientHeight + 1
		) {
			return parent;
		}
		parent = parent.parentElement;
	}
	const root = document.scrollingElement;
	return root instanceof HTMLElement ? root : null;
}

/**
 * Smoothly scroll so `el` sits near the top of its scrollport, minus padding.
 * Prefer this over bare `scrollIntoView` inside nested app shells
 * (`EntityPageShell` overflow-y-auto, etc.).
 */
export function scrollEditableFieldIntoView(
	el: HTMLElement | null | undefined,
	options?: { topPaddingPx?: number },
): void {
	if (!el) {
		return;
	}

	const topPaddingPx =
		options?.topPaddingPx ?? EDITABLE_FIELD_SCROLL_TOP_PADDING_PX;
	const scrollParent = getEditableFieldScrollParent(el);

	if (!scrollParent) {
		el.scrollIntoView({
			behavior: "smooth",
			block: "start",
			inline: "nearest",
		});
		return;
	}

	const parentRect = scrollParent.getBoundingClientRect();
	const elRect = el.getBoundingClientRect();
	const nextTop =
		scrollParent.scrollTop + (elRect.top - parentRect.top) - topPaddingPx;

	scrollParent.scrollTo({
		top: Math.max(0, nextTop),
		behavior: "smooth",
	});
}
